"use client";

import { useEffect, useRef, useState } from "react";
import styled, { type CSSProperties } from "styled-components";
import Image from "next/image";
import Chevron from "@/assets/icons/Chevron.svg";
import Checkbox from "../Checkbox/Checkbox";
import { createPortal } from "react-dom";
import { useBoundStore } from "@/lib/stores/useBoundStore";

export type IDropdownOption = {
  value: string | number | null;
  name: string;
};

export type DropdownSelectEvent =
  | React.MouseEvent<HTMLLIElement>
  | React.MouseEvent<HTMLDivElement>
  | React.ChangeEvent<HTMLInputElement>
  | ISyntheticEvent;

interface ISyntheticEvent {
  target: {
    getAttribute: (attr: string) => string | null;
    dataset: {
      value: string;
    };
  };
  currentTarget: {
    getAttribute: (attr: string) => string | null;
    dataset: {
      value: string;
    };
  };
  preventDefault: () => void;
  stopPropagation: () => void;
}

interface DropdownProps {
  type?: string;
  styles?: CSSProperties;
  options?: IDropdownOption[];
  dropdownTitle: string;
  selectedOption: string;
  disabled?: boolean;
  onSelect: (e: DropdownSelectEvent) => void;
  inModal?: boolean;
  color: string;
  backgroundColor: string;
  selectedOptions?: string[];
  required?: boolean;
  paginationFooter?: React.ReactNode;
  loadingState?: React.ReactNode;
  isInfiniteQueryFetching?: boolean;
  onDropdownOpen?: (scrollContainer: HTMLUListElement | null) => void;
}

interface IDropdown {
  $disabled?: boolean;
  $error?: boolean;
  $isOpened: boolean;
  $isView?: boolean;
  $color: string;
  $backgroundColor: string;
}

const StyledDropdown = styled.div<IDropdown>`
  display: flex;
  flex-direction: column;
  height: fit-content;
  max-height: 40px;
  max-width: 300px;
  width: 100%;
  min-width: 200px;
  position: relative;
  flex: 1;

  .title {
    position: absolute;
    top: -14px;
    left: 12px;
    z-index: 1;
    padding: 0px 4px;
    font-size: 0.8rem;
    text-align: left;
    font-weight: 400;
    background-color: ${(props) => props.$backgroundColor};
    color: ${(props) =>
      props.$disabled ? `${props.theme.lightGrey}` : `${props.theme.black}`};

    span {
      color: ${(props) => props.theme.main};
    }
  }

  .row {
    background-color: #fff;
    border: 1px solid
      ${(props) =>
        props.$disabled ? `${props.theme.lightGrey}` : `${props.$color};`};
    border-radius: 5px;
    position: relative;
    cursor: pointer;
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 9px 10px;
  }

  .option {
    font-size: 1rem;
    color: ${(props) => (props.$disabled ? "#d5d5d5" : "#141414")};
    text-wrap: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .arrow {
    object-fit: cover;
    height: 10px;
    filter: ${(props) => (props.$disabled ? "opacity(0.3)" : "none")};
  }

  #opened {
    transform: rotate(180deg);
  }
`;

const StyledUl = styled.ul<{ $isInfiniteQueryFetching?: boolean }>`
  font-size: 1rem;
  height: fit-content;
  overflow-y: auto;
  list-style-type: none;
  padding: 0;
  margin: 0;
  max-height: 180px;

  ${(props) =>
    props.$isInfiniteQueryFetching &&
    `
      min-height: 50px;
    `}

  li {
    cursor: pointer;
    padding: 10px 10px;
    font-weight: 500;
    color: ${(props) => props.theme.black};
    word-break: break-word;

    &:hover {
      color: ${(props) => props.theme.main};
    }
  }

  @media (max-width: 1024px) {
    font-size: 0.85rem;
  }
`;

const StyledCheckboxOption = styled.div`
  display: flex;
  gap: 10px;
  padding: 5px 10px;
  font-size: 1rem;
  cursor: pointer;
  color: ${(props) => props.theme.black};
  word-break: break-word;

  &:hover {
    color: ${(props) => props.theme.main};
  }

  @media (max-width: 1024px) {
    font-size: 0.85rem;
  }
`;

export function Dropdown({
  styles,
  dropdownTitle,
  selectedOption,
  options,
  disabled = false,
  onSelect,
  inModal = false,
  color,
  backgroundColor,
  type = "dropdown",
  selectedOptions,
  required,
  paginationFooter,
  loadingState,
  isInfiniteQueryFetching,
  onDropdownOpen,
}: DropdownProps) {
  const [selected, setSelectedOption] = useState(selectedOption);
  const [isToggled, setIsToggled] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const rowRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const ulRef = useRef<HTMLUListElement>(null);
  const isModalOpen = useBoundStore((state) => state.isModalOpen);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isToggled) return;

      const target = event.target as Node;

      const clickedOutsideTrigger =
        rowRef.current && !rowRef.current.contains(target);

      const clickedOutsideDropdown =
        dropdownRef.current && !dropdownRef.current.contains(target);

      if (clickedOutsideTrigger && clickedOutsideDropdown) {
        setIsToggled(false);
        setFocusedIndex(-1);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isToggled]);

  useEffect(() => {
    if (isToggled && ulRef.current && onDropdownOpen) {
      onDropdownOpen(ulRef.current);
    }
  }, [isToggled, onDropdownOpen]);

  useEffect(() => {
    setSelectedOption(selectedOption);
  }, [selectedOption]);

  useEffect(() => {
    if (selectedOption) {
      const foundName = options?.find(
        (option) => option.value === selectedOption.toString(),
      );
      if (foundName) {
        setSelectedOption(foundName.name);
      } else {
        setSelectedOption(selectedOption);
      }
    }
  }, [selectedOption]);

  useEffect(() => {
    function updatePosition() {
      if (rowRef.current) {
        const rect = rowRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    }

    if (isToggled) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isToggled]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
      case " ": // Space
        e.preventDefault();
        if (!isToggled) {
          setIsToggled(true);
          setFocusedIndex(0);
        } else if (focusedIndex >= 0) {
          if (options && options?.length > 0) {
            // Select the focused option
            const option = options[focusedIndex];
            console.log("Selected option:", option);
            console.log("Option value:", option.value);
            console.log("Option name:", option.name);

            // Create a proper synthetic event that matches what onClick would provide
            const syntheticEvent = {
              target: {
                getAttribute: (attr: string) => {
                  if (attr === "data-value") {
                    return option.value?.toString() || option.name || "";
                  }
                  return null;
                },
                dataset: {
                  value: option.value?.toString() || option.name || "",
                },
              },
              currentTarget: {
                getAttribute: (attr: string) => {
                  if (attr === "data-value") {
                    return option.value?.toString() || option.name || "";
                  }
                  return null;
                },
                dataset: {
                  value: option.value?.toString() || option.name || "",
                },
              },
              preventDefault: () => {},
              stopPropagation: () => {},
            } as ISyntheticEvent;

            console.log("Calling onSelect with synthetic event");
            onSelect(syntheticEvent);
            setIsToggled(false);
            setSelectedOption(option.name);
            rowRef.current?.focus();
          } else {
            setIsToggled(false);
            setFocusedIndex(-1);
            rowRef.current?.focus();
          }
        }

        break;

      case "Escape":
        if (isToggled) {
          e.preventDefault();
          setIsToggled(false);
          setFocusedIndex(-1);
          rowRef.current?.focus();
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        if (!isToggled) {
          setIsToggled(true);
          setFocusedIndex(0);
        } else if (options && focusedIndex < options.length - 1) {
          setFocusedIndex(focusedIndex + 1);
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (isToggled && focusedIndex > 0) {
          setFocusedIndex(focusedIndex - 1);
        }
        break;

      case "Tab":
        if (isToggled) {
          setIsToggled(false);
          setFocusedIndex(-1);
        }
        break;
    }
  };

  // Scroll focused item into view
  useEffect(() => {
    if (isToggled && focusedIndex >= 0 && ulRef.current) {
      const focusedItem = ulRef.current.children[focusedIndex] as HTMLElement;
      if (focusedItem) {
        focusedItem.scrollIntoView({ block: "nearest" });
      }
    }
  }, [focusedIndex, isToggled]);

  return (
    <StyledDropdown
      $disabled={disabled}
      $isOpened={isToggled}
      $color={color}
      $backgroundColor={backgroundColor}
      style={styles}
    >
      <div className="title">
        {dropdownTitle}
        {required && <span>*</span>}
      </div>
      <div
        className="row"
        ref={rowRef}
        onClick={() => !disabled && setIsToggled(!isToggled)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isToggled}
        aria-haspopup="listbox"
        aria-label={dropdownTitle}
      >
        <div className="option">{selected}</div>
        <Image
          src={Chevron.src}
          className="arrow"
          alt="chevron"
          id={isToggled ? "opened" : ""}
          width={19}
          height={12}
        />
      </div>

      {isToggled &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: dropdownPos.top - 2,
              left: dropdownPos.left,
              width: dropdownPos.width,
              background: "#fff",
              border: `1px solid ${color}`,
              borderRadius: 4,
              zIndex: inModal ? 9999 : isModalOpen ? "unset" : 9999,
            }}
          >
            {type === "dropdown" ? (
              <>
                <StyledUl
                  ref={ulRef}
                  $isInfiniteQueryFetching={isInfiniteQueryFetching}
                  role="listbox"
                >
                  {loadingState}
                  {options!.map((option: IDropdownOption, idx) => {
                    return (
                      <li
                        title={option.value?.toString() || option.name}
                        role="option"
                        aria-selected={focusedIndex === idx}
                        data-value={option.value || option.name || option}
                        key={`opt-${option.value || option.name || option}`}
                        onClick={(e) => {
                          onSelect(e);
                          setIsToggled(!isToggled);
                          setSelectedOption(option.name);
                          rowRef.current?.focus();
                        }}
                        style={{
                          backgroundColor:
                            focusedIndex === idx ? "#f0f0f0" : "transparent",
                        }}
                      >
                        {option.name}
                      </li>
                    );
                  })}
                  {paginationFooter}
                </StyledUl>
              </>
            ) : (
              <>
                <StyledUl
                  ref={ulRef}
                  $isInfiniteQueryFetching={isInfiniteQueryFetching}
                  role="listbox"
                >
                  {loadingState}
                  {options!.map((option: IDropdownOption, idx) => {
                    return (
                      <StyledCheckboxOption
                        className="dropdown-check"
                        key={`checkbox-value-${idx}`}
                        data-value={option.value != null ? option.value.toString() : "unknown"}
                        role="option"
                        aria-selected={focusedIndex === idx}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onSelect(e);
                          setSelectedOption(
                            option.value?.toString() || option.name,
                          );
                        }}
                        style={{
                          backgroundColor:
                            focusedIndex === idx ? "#f0f0f0" : "transparent",
                        }}
                      >
                        <Checkbox
                          label={option.name}
                          toggled={
                            selectedOptions
                              ? (selectedOptions.length > 0 &&
                                  selectedOptions?.some(
                                    (selected) => selected === option.value,
                                  )) ||
                                selectedOptions[0] === "all"
                              : false
                          }
                          clickedFunction={() => {}}
                        />
                      </StyledCheckboxOption>
                    );
                  })}
                  {paginationFooter}
                </StyledUl>
              </>
            )}
          </div>,
          document.body,
        )}
    </StyledDropdown>
  );
}
